'use client';

import cn from 'classnames';
import { ComponentRef, forwardRef, useId } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { RadioGroupItemProps, Text } from '@/shared/ui/core';
import styles from './radio-group.module.scss';


export const RadioGroupItem = forwardRef<ComponentRef<typeof RadioGroupPrimitive.Item>, RadioGroupItemProps>(function RadioGroupItem ({
    className,
    wrapperClassName,
    label,
    description,
    state = 'default',
    disabled,
    ...props
}, ref) {
    const _id           = useId();
    const id            = props.id ?? _id;
    const describedBy   = description ? `${ id }-description` : undefined;
    const isError       = state === 'error';
    const isSuccess     = state === 'success';
    const isWarning     = state === 'warning';
    const isDefault     = state === 'default';
    const textClassname = cn({
        [styles.default]    : isDefault,
        [styles.destructive]: isError,
        [styles.success]    : isSuccess,
        [styles.warning]    : isWarning,
        [styles.disabled]   : disabled,
    });

    return (
        <div className={ cn(styles.item_wrapper, wrapperClassName) }>
            <RadioGroupPrimitive.Item
                ref={ ref }
                disabled={ disabled }
                aria-describedby={ describedBy }
                className={ cn(styles.item, textClassname, className) }
                { ...props }
                id={ id }
            >
                <RadioGroupPrimitive.Indicator className={ styles.indicator }/>
            </RadioGroupPrimitive.Item>

            {
                (label || description) &&
                <div className={ styles.label_wrapper }>
                    {
                        label &&
                        <Text
                            as="label"
                            size="lg"
                            tone="primary"
                            htmlFor={ id }
                            lineHeight={ 1 }
                            weight='medium'
                            className={ cn(styles.label, textClassname) }
                        >
                            { label }
                        </Text>
                    }

                    {
                        description &&
                        <Text
                            tone="secondary"
                            id={ describedBy }
                            lineHeight={ 1.2 }
                            className={ styles.description }
                        >
                            { description }
                        </Text>
                    }
                </div>
            }
        </div>
    );
});
